import { EditorFeatures } from '@src/ui/Editor';
import {
  BlockquoteButton,
  BoldButton,
  BulletListButton,
  ItalicButton,
  LinkButton,
  OrderedListButton,
  StrikeButton,
} from '@src/ui/Editor/components/toolbars/buttons';
import { ActiveState, getActiveState, initialActiveState } from '@src/ui/Editor/utils/activeState';
import type { Editor } from '@tiptap/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface CommunityToolbarProps {
  editor: Editor | null;
  features: EditorFeatures;
  eventHandlers: {
    onSelectFile?: (files: FileList) => void;
    handleOpenLinkAddModal?: () => void;
  };
}

/**
 * CommunityToolbar
 * Specialized toolbar for Community context with restricted features
 */
export const CommunityToolbar: React.FC<CommunityToolbarProps> = ({
  editor,
  features,
  eventHandlers,
}) => {
  const [activeState, setActiveState] = useState<ActiveState>(initialActiveState);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refreshActiveState = useCallback(() => {
    if (!editor) {
      return;
    }
    if (editor.view.composing) {
      return;
    }
    setActiveState(getActiveState(editor));
  }, [editor]);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && eventHandlers?.onSelectFile) {
        eventHandlers.onSelectFile(files);
      }
      event.target.value = '';
    },
    [eventHandlers],
  );

  useEffect(() => {
    if (!editor) {
      return;
    }

    refreshActiveState();
    editor.on('selectionUpdate', refreshActiveState);
    editor.on('update', refreshActiveState);

    return () => {
      editor.off('selectionUpdate', refreshActiveState);
      editor.off('update', refreshActiveState);
    };
  }, [editor, refreshActiveState]);

  if (!editor) {
    return null;
  }

  return (
    <div className='flex flex-col border-b'>
      {/* Hidden file input */}
      <input
        type='file'
        multiple
        className='hidden'
        ref={fileInputRef}
        onChange={handleFileChange}
      />

      {/* Community formatting toolbar */}
      {features.richText && (
        <div className='toolbar flex flex-wrap gap-1 bg-blue-50 p-2'>
          {/* Bold */}
          {features.textStyles && (
            <BoldButton
              editor={editor}
              isActive={activeState.bold}
              refreshActiveState={refreshActiveState}
            />
          )}

          {/* Italic */}
          {features.textStyles && (
            <ItalicButton
              editor={editor}
              isActive={activeState.italic}
              refreshActiveState={refreshActiveState}
            />
          )}

          {/* Strike */}
          {features.textStyles && (
            <StrikeButton
              editor={editor}
              isActive={activeState.strike}
              refreshActiveState={refreshActiveState}
            />
          )}

          {/* Bullet List */}
          {features.lists && (
            <BulletListButton
              editor={editor}
              isActive={activeState.bulletList}
              refreshActiveState={refreshActiveState}
            />
          )}

          {/* Ordered List */}
          {features.lists && (
            <OrderedListButton
              editor={editor}
              isActive={activeState.orderedList}
              refreshActiveState={refreshActiveState}
            />
          )}

          {/* Link */}
          {features.links && (
            <LinkButton
              isActive={activeState.link}
              refreshActiveState={refreshActiveState}
              onOpenLinkAddModal={() => eventHandlers?.handleOpenLinkAddModal?.()}
            />
          )}

          {/* Blockquote */}
          {features.quotes && (
            <BlockquoteButton
              editor={editor}
              isActive={activeState.blockquote}
              refreshActiveState={refreshActiveState}
            />
          )}
        </div>
      )}
    </div>
  );
};
